/**
 * Asking for a reset link.
 *
 * The screen deliberately gives the same answer to every address, because the
 * server does: telling somebody "no account here" turns this form into an
 * oracle for refining a ten-million-address credential list down to the few
 * thousand that are real.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { AuthLayout } from './AuthLayout';
import { Button, Input } from '../../components/ui';

export function ForgotPasswordPage(): ReactNode {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (caught) {
      // Only a genuine failure to reach the server lands here — an unknown
      // address is a 202 like any other.
      setError(caught instanceof ApiError ? caught.message : 'Could not send the link.');
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <AuthLayout title="Check your email">
        <p className="text-sm text-slate-600">
          If that address has a CenoX account, a reset link is on its way. It works once and
          expires shortly, so use it soon.
        </p>
        <p className="mt-4 text-sm text-slate-500">
          Nothing arrived? Check spam, then try again — the address may be different from the one
          you signed up with.
        </p>
        <Button className="mt-6 w-full" variant="secondary" onClick={() => setSent(false)}>
          Try another address
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Reset your password"
      subtitle="We will email you a link to choose a new one"
      footer={
        <>
          Remembered it?{' '}
          <Link to="/login" className="font-medium text-brand-600 hover:text-brand-700">
            Sign in instead
          </Link>
        </>
      }
    >
      <form className="space-y-4" onSubmit={submit} noValidate>
        {error && (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Button type="submit" size="lg" loading={submitting} className="w-full">
          Send reset link
        </Button>
      </form>
    </AuthLayout>
  );
}
